import { useId } from 'react';

import { PricelistProductDisplayOptionsData } from '../../../../app/data/model';
import { ProductModelPricelistTranslation } from '../../../localization/ModuleTranslation';
import type { PricelistPriceGroupData } from '../../../model';
import NetPrice from '../../../shared/NetPrice/NetPrice';
import Badge from '../../../ui/Badge/Badge';
import Price from '../../../ui/Price/Price';
import { Cell, HeaderCell, Row, Table, TableBody, TableHead } from '../../../ui/Table/Table';
import css from './ProductModelPricelist.css';

interface ProductModelPricelistProps {
  displayOptions: PricelistProductDisplayOptionsData;
  priceGroups: PricelistPriceGroupData[];
  translation: ProductModelPricelistTranslation;
  basePrice?: number;
  netBasePrice?: number;
  /** Missing when the dealer has no agreement for the product. */
  agreementDiscount?: number;
}

const ProductModelPricelist = ({
  agreementDiscount,
  basePrice,
  netBasePrice,
  priceGroups,
  translation,
  displayOptions,
}: ProductModelPricelistProps) => {
  const titleId = useId();
  const hasPriceOnDemand = displayOptions.showPriceOnDemand;
  const hasBasePrice = !hasPriceOnDemand && !!basePrice;
  const hasPriceGroups = !hasPriceOnDemand && priceGroups.any();

  if (!hasBasePrice && !hasPriceGroups && !hasPriceOnDemand) {
    return null;
  }

  return (
    <div className={css.root}>
      <div className={css.summary}>
        <span id={titleId} className={css.title}>
          {translation.caption}
        </span>
        {!hasPriceOnDemand && (
          <span className={css.discount}>
            <span className={css.discountLabel}>{translation.agreementDiscount}</span>
            <span className={css.discountValue}>
              {agreementDiscount == null ? translation.netPrice.notAvailable : `${agreementDiscount}%`}
            </span>
          </span>
        )}
      </div>
      <Table aria-labelledby={titleId}>
        <TableHead>
          <Row>
            <HeaderCell>{translation.productGroup}</HeaderCell>
            <HeaderCell className={css.priceColumn}>{translation.price}</HeaderCell>
            {!hasPriceOnDemand && <HeaderCell className={css.priceColumn}>{translation.netPrice.label}</HeaderCell>}
          </Row>
        </TableHead>
        <TableBody>
          {hasPriceGroups &&
            priceGroups.map((item, index) => (
              <Row key={index}>
                <Cell>
                  <Badge>{item.name}</Badge>
                </Cell>
                <Cell className={css.priceColumn}>
                  <Price amount={item.listPrice} />
                </Cell>
                <Cell className={css.priceColumn}>
                  <NetPrice amount={item.netPrice} translation={translation.netPrice} />
                </Cell>
              </Row>
            ))}
          {(hasBasePrice || hasPriceOnDemand) && (
            <Row>
              <Cell>
                <Badge>{translation.basePrice}</Badge>
              </Cell>
              <Cell className={css.priceColumn}>
                {hasPriceOnDemand ? translation.priceOnDemand : <Price amount={basePrice} />}
              </Cell>
              {!hasPriceOnDemand && (
                <Cell className={css.priceColumn}>
                  <NetPrice amount={netBasePrice} translation={translation.netPrice} />
                </Cell>
              )}
            </Row>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ProductModelPricelist;
